<!--
 * Path: packages/frontend/src/core/components/entityHeader/EntityHeaderTitlePanel.vue
 * Task: Renders the entity icon, main title, timestamp, and the entity ID.
 * Update: Reusing shared cycle detection logic to natively suggest "Resolve Cycle" actions inside the header.
-->
<template>
  <div class="d-flex align-center min-w-0 w-100 title-edit-wrapper" :class="{ 'is-editing': isEditing }" v-if="displayEntity">
    <EntityIcon
      :entity="displayEntity"
      size="32"
      class="me-3 flex-shrink-0"
    />

    <!-- Text Column Wrapper (Title + ID) -->
    <div class="d-flex flex-column flex-grow-1 min-w-0">

      <!-- Read Mode -->
      <div v-if="!isEditing" class="d-flex align-center min-w-0 w-100">
        <v-btn
          v-if="canEditWorkspace"
          icon
          variant="text"
          size="small"
          color="medium-emphasis"
          class="me-3 transition-swing inline-edit-btn flex-shrink-0"
          @click="toggleEdit"
          :disabled="uiStore.loading.save"
        >
          <v-icon size="small">mdi-pencil-outline</v-icon>
          <v-tooltip activator="parent" location="top">Edit Title</v-tooltip>
        </v-btn>

        <v-tooltip location="top" open-delay="400">
          <template v-slot:activator="{ props: tooltipProps }">
            <h1
              v-bind="tooltipProps"
              class="text-h4 text-high-emphasis text-truncate font-weight-bold mb-0"
              style="line-height: 1.2; cursor: help;"
            >
              {{ displayEntity.title || 'Untitled Element' }}
            </h1>
          </template>
          <span>{{ displayEntity.title || 'Untitled Element' }}</span>
        </v-tooltip>

        <!-- Actionable Analytics: Suggestions Menu (Zauberstab) -->
        <div v-if="(cycleSuggestion || bottleneckSeverity > 0) && canEditWorkspace" class="ms-3 flex-shrink-0">
          <v-menu v-model="suggestionMenuOpen" :close-on-content-click="false" location="bottom" offset="8">
            <template v-slot:activator="{ props }">
              <v-badge
                :model-value="!!cycleSuggestion"
                dot
                color="error"
                offset-x="6"
                offset-y="6"
              >
                <v-btn
                  v-bind="props"
                  icon
                  color="suggestion"
                  variant="tonal"
                  size="small"
                  class="pulse-suggestion"
                >
                  <v-icon>mdi-auto-fix</v-icon>
                  <v-tooltip activator="parent" location="bottom">Auto-Refactoring Suggestions</v-tooltip>
                </v-btn>
              </v-badge>
            </template>

            <!-- Dropdown Content: Suggestion List -->
            <v-card min-width="320" max-width="360" class="rounded-xl elevation-4 border">
              <div class="pa-3 border-b d-flex align-center bg-surface-light">
                <v-icon color="suggestion" class="me-2">mdi-auto-fix</v-icon>
                <span class="font-weight-bold text-subtitle-2">Auto-Refactoring</span>
              </div>

              <div class="d-flex flex-column">
                <!-- 1. Cycle Suggestion -->
                <div v-if="cycleSuggestion" class="pa-3" :class="{ 'border-b': bottleneckSeverity > 0 }" style="background-color: rgba(var(--v-theme-error), 0.03);">
                  <div class="d-flex align-start mb-3">
                    <v-icon size="16" color="error" class="me-2 mt-1 flex-shrink-0">mdi-sync-alert</v-icon>
                    <div class="text-caption text-medium-emphasis" style="line-height: 1.4;">
                      <strong>Circular Dependency Detected.</strong><br>
                      Break the feedback loop by removing the dependency to <strong class="text-uppercase">{{ cycleSuggestion.target.title || cycleSuggestion.target.id }}</strong>.
                    </div>
                  </div>
                  <v-btn
                    color="suggestion"
                    variant="tonal"
                    size="small"
                    class="font-weight-bold text-none w-100"
                    :loading="isResolvingCycle === displayEntity.id"
                    @click="handleResolveCycle"
                  >
                    <v-icon start size="small">mdi-link-variant-off</v-icon>
                    Resolve Cycle
                  </v-btn>
                </div>

                <!-- 2. Split Suggestion -->
                <div v-if="bottleneckSeverity > 0" class="pa-3" style="background-color: rgba(var(--v-theme-on-surface), 0.02);">
                  <div class="d-flex align-start mb-3">
                    <v-icon size="16" color="suggestion" class="me-2 mt-1 flex-shrink-0">mdi-sitemap-outline</v-icon>
                    <div class="text-caption text-medium-emphasis" style="line-height: 1.4;">
                      <strong>High incoming complexity</strong> ({{ bottleneckSeverity }} predecessors).<br>
                      Split this entity into "Start" and "End" nodes to parallelize downstream workflows.
                    </div>
                  </div>
                  <v-btn
                    color="suggestion"
                    variant="tonal"
                    size="small"
                    class="font-weight-bold text-none w-100"
                    :loading="isSplitting === displayEntity.id"
                    @click="handleSplit"
                  >
                    <v-icon start size="small">mdi-call-split</v-icon>
                    Suggest Split
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-menu>
        </div>

        <!-- Inject Pill Buttons right next to the title in Read Mode -->
        <div v-if="pillRelations.length > 0" class="ms-3 flex-shrink-0 d-flex align-center" style="gap: 8px;">
          <PillButton
            v-for="pill in pillRelations"
            :key="pill.id"
            :field="pill.asField"
          />
        </div>
      </div>

      <!-- Edit Mode -->
      <div v-else class="d-flex align-center w-100 pe-2">
        <v-btn
          icon
          variant="tonal"
          size="small"
          color="error"
          class="me-1 flex-shrink-0"
          @click="cancelEdit"
          :disabled="uiStore.loading.save"
        >
          <v-icon size="small">mdi-close</v-icon>
          <v-tooltip activator="parent" location="top">Cancel</v-tooltip>
        </v-btn>

        <v-btn
          icon
          variant="tonal"
          size="small"
          color="primary"
          class="me-3 flex-shrink-0"
          @click="saveChanges"
          :loading="uiStore.loading.save"
        >
          <v-icon size="small">mdi-check</v-icon>
          <v-tooltip activator="parent" location="top">Save Title</v-tooltip>
        </v-btn>

        <v-text-field
          ref="titleInputRef"
          v-model="titleProxy"
          density="compact"
          variant="outlined"
          hide-details
          class="flex-grow-1 title-input"
          autofocus
          @keydown.enter="saveChanges"
          @keydown.esc="cancelEdit"
        ></v-text-field>
      </div>

      <!-- Entity Timestamp and ID (Always visible below title/input) -->
      <div class="d-flex align-center mt-1 text-caption text-medium-emphasis" style="min-width: 0; font-family: monospace; font-size: 0.75rem !important;">
        <!-- Timestamp -->
        <div v-if="formattedDate" class="d-flex align-center me-3 flex-shrink-0" style="opacity: 0.8;">
          <v-icon size="12" class="me-1">mdi-clock-outline</v-icon>
          <span>{{ formattedDate }}</span>
          <!-- Tooltip for detailed audit info -->
          <v-tooltip activator="parent" location="bottom" open-delay="300">
            <div class="d-flex flex-column text-caption">
              <div class="d-flex align-center mb-1">
                <v-icon size="small" color="white" class="me-2">mdi-clock-time-four-outline</v-icon>
                <span>{{ formattedDateTime }}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon size="small" color="white" class="me-2">mdi-account-edit-outline</v-icon>
                <span>{{ updatedByText }}</span>
              </div>
            </div>
          </v-tooltip>
        </div>

        <!-- Entity ID -->
        <div class="d-flex align-center text-truncate" style="opacity: 0.8;">
          <v-icon size="12" class="me-1">mdi-pound</v-icon>
          <span>{{ displayEntity.id }}</span>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useDatastoreStore } from '@/store/datastore';
import { usePermissions } from '@/core/composables/usePermissions';
import { useEntityOperations, type CycleBreak } from '@/core/composables/useEntityOperations';
import type { Entity } from '@/core/types/editor';
import { FormContextKey } from '@/core/keys';

import EntityIcon from '@/core/components/common/EntityIcon.vue';
import PillButton from '@/core/components/forms/pillButton/PillButton.vue';
import { useEntitySchema } from '@/features/browser/composables/useEntitySchema';

interface Props {
  entity?: Entity | null;
  isSecondary?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  entity: null,
  isSecondary: false
});

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const datastoreStore = useDatastoreStore();
const { canEditWorkspace } = usePermissions();
const { saveEntity, splitEntity, getBottleneckSeverity, detectCycles, resolveCycleEdge } = useEntityOperations();
const formContext = inject(FormContextKey, null);

const titleInputRef = ref<any>(null);

const displayEntity = computed<Entity | null>(() => {
  if (props.entity) return props.entity;
  if (formContext) return formContext.entity.value;
  return props.isSecondary
    ? workspaceStore.secondarySelectedEntity
    : workspaceStore.selectedEntity;
});

const isEditing = ref(false);
const isSplitting = ref<string | null>(null);
const isResolvingCycle = ref<string | null>(null);
const suggestionMenuOpen = ref(false);

const isLocked = computed<boolean>(() => {
  if (formContext) return formContext.isLocked.value;
  return workspaceStore.isLocked;
});

// Fetch all schema definitions for the current entity
const { combinedSchema } = useEntitySchema(displayEntity, isLocked);

// Filter ONLY the relations that are defined as 'pill_button' in the Data Settings
const pillRelations = computed(() => {
  if (!combinedSchema.value) return [];
  return combinedSchema.value.filter((item: any) =>
    item._kind === 'relation' && item.uiPresentation === 'pill_button'
  );
});

// --- Actionable Analytics: Bottleneck Refactoring ---
const bottleneckSeverity = computed(() => getBottleneckSeverity(displayEntity.value));

const handleSplit = async () => {
  if (!displayEntity.value) return;
  if (!confirm(`Do you want to safely split "${displayEntity.value.title || displayEntity.value.id}" into a Start and End entity to parallelize downstream work?`)) {
    return;
  }
  isSplitting.value = displayEntity.value.id;
  try {
    await splitEntity(props.isSecondary || (formContext?.isSecondary ?? false));
    suggestionMenuOpen.value = false;
  } finally {
    isSplitting.value = null;
  }
};

// --- Actionable Analytics: Cycle Breaker ---
const cycles = computed(() => detectCycles());

const cycleSuggestion = computed<CycleBreak | null>(() => {
  if (!displayEntity.value) return null;
  // Does this entity act as the SOURCE for any suggested break?
  const cycle = cycles.value.find((c: any) => c.suggestedBreak.source.id === displayEntity.value!.id);
  return cycle ? cycle.suggestedBreak : null;
});

const handleResolveCycle = async () => {
  if (!cycleSuggestion.value || !displayEntity.value) return;
  isResolvingCycle.value = displayEntity.value.id;
  try {
    await resolveCycleEdge(cycleSuggestion.value.source, cycleSuggestion.value.target);
    suggestionMenuOpen.value = false;
  } finally {
    isResolvingCycle.value = null;
  }
};

// --- Helper for Robust Date Parsing ---
const parseSafeDate = (dateStr?: string) => {
  if (!dateStr) return null;
  let safeStr = dateStr;
  if (safeStr.includes(' ') && !safeStr.includes('T')) {
    safeStr = safeStr.replace(' ', 'T') + 'Z';
  }
  const d = new Date(safeStr);
  return isNaN(d.getTime()) ? null : d;
};

// --- Time and Audit Logic ---
const formattedDate = computed<string>(() => {
  const date = parseSafeDate(displayEntity.value?.lastUpdated);
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
});

const formattedDateTime = computed<string>(() => {
  const date = parseSafeDate(displayEntity.value?.lastUpdated);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

const updatedByText = computed<string>(() => {
  return displayEntity.value?.updatedBy || 'System / Unknown';
});

// --- Edit Logic ---
watch(() => displayEntity.value, (newEntity) => {
  if (newEntity) {
    const isDraft = !datastoreStore.allEntities.some((e: Entity) => e.id === newEntity.id);
    if (isDraft && canEditWorkspace.value) {
      isEditing.value = true;
    } else {
      isEditing.value = false;
    }
  } else {
    isEditing.value = false;
  }
}, { immediate: true });

watch(isEditing, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      if (titleInputRef.value) {
        titleInputRef.value.focus();
        // Grab native input element and force text selection
        const inputEl = titleInputRef.value.$el?.querySelector('input');
        if (inputEl) {
          inputEl.select();
        }
      }
    }, 300);
  }
});

const titleProxy = computed({
  get: () => displayEntity.value?.title || '',
  set: (val: string) => {
    if (displayEntity.value) {
      displayEntity.value.title = val;
    }
  }
});

const toggleEdit = () => {
  if (isEditing.value) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || props.isSecondary);
  }
  isEditing.value = !isEditing.value;
};

const cancelEdit = () => {
  workspaceStore.resetSelectedEntity(formContext?.isSecondary || props.isSecondary);
  isEditing.value = false;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || props.isSecondary);
  isEditing.value = false;
};

</script>

<style scoped>
.title-edit-wrapper {
  transition: all 0.3s ease;
}

.inline-edit-btn {
  opacity: 0.2;
  transition: opacity 0.2s ease, transform 0.3s ease;
}

.title-edit-wrapper:hover .inline-edit-btn {
  opacity: 1;
}

.title-input :deep(.v-field__input) {
  font-size: 1.15rem !important;
  font-weight: 700 !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  min-height: 40px !important;
}

/* Subtle pulsing animation to draw attention to the auto-refactor suggestion */
.pulse-suggestion {
  animation: subtlePulse 2s infinite ease-in-out;
}

@keyframes subtlePulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--v-theme-suggestion), 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(var(--v-theme-suggestion), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-suggestion), 0); }
}
</style>
